/**
 * The most a perpetual order may be for.
 *
 * Ported from `computeBuyingPower`, which answered this in floats and answered
 * **0** whenever it could not answer at all — an account still loading, a
 * `withdrawable: null`, a fee schedule the backend had not verified. A 0 max
 * reads as "you have nothing", which is a different and much worse statement
 * than "not yet known". Every one of those cases now returns a reason.
 *
 * The maths themselves are unchanged:
 *
 *     buying power = withdrawable / (1 / leverage + feeRate)
 *
 * with market orders holding back `MARKET_ORDER_MARGIN_BUFFER_BPS` (50bps) for
 * the oracle movement between sizing and execution. What is new is that the
 * arithmetic is exact, and that an OPPOSITE position adds its own size: closing
 * it frees its margin, so the trader can flip in a single order — the old form
 * refused a flip it would have accepted.
 */
import { BookLevels } from "../book";
import { Dec, dec } from "../decimal";
import { MarketSteps } from "../spec";
import { heldAmount } from "./closePositionMax";
import { applyOrderSizeLimits, resolveOrderPricer } from "./orderLimits";
import { MaxResult, maxOk, maxUnavailable } from "./types";

/** `PerpsTradeForm.helpers.ts`: 0.5% held back on a market order. */
export const DEFAULT_MARKET_BUFFER_BPS = 50;

/** Base sizes are 1e18 everywhere on this exchange. */
const SIZE_DECIMALS = 18;
/** A quote amount is money: cents, never the market's price step. */
const QUOTE_DECIMALS = 2;

export interface PerpOrderMaxInput {
  side: "buy" | "sell";
  orderType: "market" | "limit";
  /** Which terms the field is in: the base asset, or USD. */
  unit: "base" | "quote";
  /** Free equity after margin. `null` is the API's "cannot value this account". */
  withdrawable?: Dec | null;
  leverage?: Dec;
  /** The rate an opening order may pay — the higher of maker and taker. */
  feeRate?: Dec | null;
  /** Margin held back. Defaults to 50bps on a market order, none on a limit. */
  bufferBps?: number;
  limitPrice?: Dec;
  book?: { bids?: BookLevels; asks?: BookLevels };
  mark?: Dec;
  steps: MarketSteps;
  /** The position held in this market; a short's `amount` is negative. */
  position?: { side: "buy" | "sell"; amount: Dec };
  reduceOnly?: boolean;
  baseSymbol?: string;
  quoteSymbol?: string;
}

const positive = (value: Dec | number | null | undefined): Dec | null => {
  if (value === null || value === undefined) return null;
  const parsed = dec.parse(value);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

/** The size of a position this order would REDUCE, or `null` when there is none. */
const reducibleSize = (input: PerpOrderMaxInput): Dec | null => {
  const held = heldAmount(input.position?.amount);
  if (held === null) return null;
  // A reduce-only order in the same direction as the position reduces nothing.
  return input.position?.side === input.side ? null : held;
};

export const perpOrderMax = (input: PerpOrderMaxInput): MaxResult => {
  const { steps } = input;
  // Without a grid there is no size to offer: the on-chain specs have not
  // resolved, and any figure would be one the exchange might refuse.
  if (positive(steps.amountStep) === null) return maxUnavailable("specs_unknown");

  const baseUnit = input.baseSymbol ?? "";
  const quoteUnit = input.quoteSymbol ?? "USDC";
  const opposite = reducibleSize(input);

  if (input.reduceOnly) {
    if (opposite === null) return maxUnavailable("no_position");
    // Floored, unlike a full close from the position row: this size goes
    // through the order path, which rejects anything off the step.
    const sized = applyOrderSizeLimits(opposite, steps, "position", baseUnit);
    return input.unit === "quote" ? inQuote(sized, null, input, quoteUnit) : sized;
  }

  const withdrawable = dec.parse(input.withdrawable ?? null);
  if (withdrawable === null) return maxUnavailable("withdrawable_unknown");

  const leverage = positive(input.leverage);
  if (leverage === null) return maxUnavailable("leverage_unknown");

  const feeRate = dec.parse(input.feeRate ?? null);
  if (feeRate === null) return maxUnavailable("fee_unknown");

  // Margin + fee per unit of notional. Ceiled, so the power it divides into can
  // only ever come out at or below the true figure.
  const marginAndFee = dec.add(
    dec.div("1", leverage, 40, "ceil") ?? "0",
    feeRate
  );
  if (!dec.gt(marginAndFee, "0")) return maxUnavailable("fee_unknown");

  const spendable = dec.gt(withdrawable, "0") ? withdrawable : "0";
  const rawPower = dec.div(spendable, marginAndFee, SIZE_DECIMALS, "floor") ?? "0";
  // A market order holds back the buffer by default. An explicit buffer
  // applies to any order: the MAX button passes one for limit orders too
  // (decision 5), while a validator ceiling leaves it out and so accepts
  // everything the exchange would.
  const bufferBps = clampBps(
    input.bufferBps ?? (input.orderType === "market" ? DEFAULT_MARKET_BUFFER_BPS : 0)
  );
  const power = dec.bps(rawPower, 10000 - bufferBps, "floor");

  const pricer = resolveOrderPricer(input);
  if (!pricer) return maxUnavailable("price_unknown");

  // Closing the opposite position frees its margin, so its size is spendable on
  // top of the buying power — a flip is one order, not two.
  const baseSize = dec.add(pricer.toBase(power), opposite ?? "0");
  const sized = applyOrderSizeLimits(baseSize, steps, "buying_power", baseUnit);
  return input.unit === "quote" ? inQuote(sized, power, input, quoteUnit) : sized;
};

const clampBps = (bps: number): number => {
  if (!Number.isFinite(bps) || bps <= 0) return 0;
  return bps > 10000 ? 10000 : bps;
};

/**
 * The same maximum in USD, floored to cents.
 *
 * Bounded by the money that is actually available (`power`, plus what closing
 * the opposite position frees) as well as by the value of the size: beyond the
 * book's depth the two disagree, and the smaller of them is the one the trader
 * can actually spend.
 */
const inQuote = (
  sized: MaxResult,
  power: Dec | null,
  input: PerpOrderMaxInput,
  unit: string
): MaxResult => {
  if (!sized.ok) return sized;
  const pricer = resolveOrderPricer(input);
  if (!pricer) return maxUnavailable("price_unknown");

  let quote = pricer.toQuote(sized.max);
  if (power !== null) {
    const opposite = reducibleSize(input);
    const spendable = dec.add(
      power,
      opposite === null ? "0" : pricer.toQuote(opposite)
    );
    quote = dec.min(quote, spendable);
  }

  const floored = dec.round(quote, QUOTE_DECIMALS, "floor");
  if (!dec.gt(floored, "0")) {
    return maxUnavailable("below_min_order", input.steps.minOrderAmount);
  }
  return maxOk(floored, sized.limitedBy, unit);
};

// Re-exported for the callers that grew up importing them from here; they live
// in `orderLimits` now, which is not named after one vertical.
export {
  applyOrderSizeLimits,
  resolveOrderPricer,
} from "./orderLimits";
export type { OrderPricingInput, Pricer } from "./orderLimits";
